"use client";

import { ReferralCodeCapture } from "@/components/referral-code-capture";
import { AboutV2 } from "@/components/tenant-landing-v2/about-v2";
import { BookingWizardV2 } from "@/components/tenant-landing-v2/booking/booking-wizard-v2";
import { CTAV2 } from "@/components/tenant-landing-v2/cta-v2";
import { FooterV2 } from "@/components/tenant-landing-v2/footer-v2";
import { HeroV2 } from "@/components/tenant-landing-v2/hero-v2";
import { ReviewsV2 } from "@/components/tenant-landing-v2/reviews-v2";
import { ServicesV2 } from "@/components/tenant-landing-v2/services-v2";
import { FullPageSkeleton } from "@/components/tenant-landing-v2/skeletons";
import { Button } from "@/components/ui/button";
import { useReviewsPagination, useTenantData } from "@/hooks/use-tenant-data";
import { AlertCircle, RefreshCw } from "lucide-react";
import { useState } from "react";

interface TenantLandingV2Props {
  tenantSlug: string;
}

export function TenantLandingV2({ tenantSlug }: TenantLandingV2Props) {
  const [bookingWizardOpen, setBookingWizardOpen] = useState(false);
  const [preSelectedServiceId, setPreSelectedServiceId] = useState<
    string | undefined
  >(undefined);

  const { data, isLoading, error, refetch } = useTenantData({
    tenantSlug,
    pollInterval: 0, // No polling - rely on Next.js ISR
    enabled: true,
  });

  const { isLoadingPage, handlePageChange } = useReviewsPagination({
    tenantSlug,
    initialPage: 1,
    limit: 6,
  });

  // Loading state
  if (isLoading) {
    return <FullPageSkeleton />;
  }

  // Error state
  if (error || !data) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-background p-4">
        <div className="max-w-md w-full text-center space-y-4">
          <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10 mb-4">
            <AlertCircle className="h-8 w-8 text-destructive" />
          </div>
          <h2 className="text-2xl font-bold text-foreground">
            Ops! Algo deu errado
          </h2>
          <p className="text-muted-foreground">
            {error || "Não foi possível carregar os dados. Tente novamente."}
          </p>
          <Button onClick={refetch} className="mt-4">
            <RefreshCw className="mr-2 h-4 w-4" />
            Tentar Novamente
          </Button>
        </div>
      </div>
    );
  }

  const { tenant, services, workingHours, reviews } = data;

  const handleBookingClick = (serviceId?: string) => {
    setPreSelectedServiceId(serviceId);
    setBookingWizardOpen(true);
  };

  return (
    <div className="min-h-dvh flex flex-col bg-background text-foreground">
      {/* Referral Code Capture */}
      <ReferralCodeCapture />

      {/* Header */}

      {/* Main Content */}
      <main id="inicio" className="flex-1">
        {/* Hero Section */}
        <HeroV2
          tenant={tenant}
          workingHours={workingHours}
          onBookingClick={handleBookingClick}
        />

        {/* About Section */}
        <div id="sobre">
          <AboutV2 tenant={tenant} workingHours={workingHours} />
        </div>

        {/* Reviews Section */}
        {reviews && data.meta.reviewsEnabled && (
          <ReviewsV2
            reviewsData={reviews}
            tenantName={tenant.name}
            onPageChange={handlePageChange}
            loading={isLoadingPage}
          />
        )}

        {/* Services Section */}
        <ServicesV2
          services={services}
          onServiceClick={(service) => handleBookingClick(service.id)}
        />

        {/* CTA Section */}
        <CTAV2 tenantName={tenant.name} onBookingClick={handleBookingClick} />
      </main>

      {/* Footer */}
      <FooterV2 tenant={tenant} />

      {/* Booking Wizard */}
      <BookingWizardV2
        open={bookingWizardOpen}
        onOpenChange={setBookingWizardOpen}
        tenantSlug={tenantSlug}
        tenantPixKey={tenant.phone}
        services={services}
        preSelectedServiceId={preSelectedServiceId}
      />
    </div>
  );
}
